import { useState } from "react";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { fmtPrice } from "../lib/i18n";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Landmark, CreditCard, Banknote } from "lucide-react";

export default function Checkout() {
    const { cart, cartTotal, lang, t, clearCart } = useApp();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [bankInfo, setBankInfo] = useState(null);
    const [cashInfo, setCashInfo] = useState(null);
    const [orderId, setOrderId] = useState(null);
    const [orderSnapshot, setOrderSnapshot] = useState(null);
    const [form, setForm] = useState({
        customer_name: "", customer_email: "", customer_phone: "",
        customer_address: "", customer_city: "", notes: "",
        payment_method: "bank_transfer",
    });
    // visual selection: "card" | "applepay" | "bank_transfer" | "cash"
    const [selectedMethod, setSelectedMethod] = useState("bank_transfer");

    const pickMethod = (visual) => {
        setSelectedMethod(visual);
        const backendMethod = visual === "card" || visual === "applepay" ? "stripe" : visual;
        setForm((f) => ({ ...f, payment_method: backendMethod }));
    };

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const buildOrderWhatsAppUrl = (oid, payMethod) => {
        const waNum = "0524110193".replace(/\D/g, "");
        const waFull = waNum.startsWith("0") ? `971${waNum.slice(1)}` : waNum;
        const payLabel = payMethod === "bank_transfer"
            ? (lang === "ar" ? "تحويل بنكي" : "Bank Transfer")
            : (lang === "ar" ? "كاش عند الاستلام" : "Cash on Delivery");
        const snap = orderSnapshot;
        if (!snap) return `https://wa.me/${waFull}`;
        const lines = [];
        lines.push(lang === "ar" ? "🌟 *طلب جديد من موقع سراء تنت*" : "🌟 *New order from SRAA TENT*");
        lines.push("");
        lines.push(`${lang === "ar" ? "📋 رقم الطلب" : "Order"}: ${oid}`);
        lines.push(`${lang === "ar" ? "👤 الاسم" : "Name"}: ${snap.customer_name}`);
        lines.push(`${lang === "ar" ? "📱 الجوال" : "Phone"}: ${snap.customer_phone}`);
        lines.push(`${lang === "ar" ? "📧 الإيميل" : "Email"}: ${snap.customer_email}`);
        lines.push(`${lang === "ar" ? "📍 العنوان" : "Address"}: ${snap.customer_address}, ${snap.customer_city}`);
        if (snap.notes) lines.push(`${lang === "ar" ? "📝 ملاحظات" : "Notes"}: ${snap.notes}`);
        lines.push("");
        lines.push(lang === "ar" ? "🛒 *المنتجات:*" : "🛒 *Items:*");
        snap.items.forEach((it) => {
            const line = `• ${it.product_name}${it.variant_label ? ` (${it.variant_label})` : ""} × ${it.quantity} = ${it.price * it.quantity} AED`;
            lines.push(line);
        });
        lines.push("");
        lines.push(`${lang === "ar" ? "💰 *الإجمالي*" : "💰 *Total*"}: ${snap.total} AED`);
        lines.push(`${lang === "ar" ? "💳 طريقة الدفع" : "Payment"}: ${payLabel}`);
        return `https://wa.me/${waFull}?text=${encodeURIComponent(lines.join("\n"))}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (cart.length === 0) { toast.error(t.cart.empty); return; }
        setLoading(true);
        try {
            const payload = {
                ...form,
                items: cart.map((it) => ({
                    product_id: it.product_id,
                    product_name: it.product_name,
                    variant_label: it.variant_label,
                    price: it.price,
                    quantity: it.quantity,
                    image: it.image,
                })),
                origin_url: window.location.origin,
            };
            const res = await api.post("/orders", payload);
            setOrderSnapshot({
                customer_name: form.customer_name,
                customer_email: form.customer_email,
                customer_phone: form.customer_phone,
                customer_address: form.customer_address,
                customer_city: form.customer_city,
                notes: form.notes,
                items: payload.items,
                total: cartTotal,
            });
            if (form.payment_method === "stripe") {
                clearCart();
                window.location.href = res.data.checkout_url;
            } else if (form.payment_method === "bank_transfer") {
                setBankInfo(res.data.bank_info);
                setOrderId(res.data.order_id);
                clearCart();
            } else if (form.payment_method === "cash") {
                setCashInfo({ whatsapp: res.data.whatsapp, total: res.data.total });
                setOrderId(res.data.order_id);
                clearCart();
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || (lang === "ar" ? "حدث خطأ" : "Error"));
        } finally {
            setLoading(false);
        }
    };

    if (bankInfo && orderId) {
        return (
            <div className="pt-32 pb-24 max-w-2xl mx-auto px-6 lg:px-10" data-testid="bank-transfer-info">
                <div className="text-center mb-10">
                    <div className="overline mb-4">{t.checkout.order_placed}</div>
                    <h1 className="section-heading text-4xl md:text-5xl text-white mb-3">{t.checkout.bank_title}</h1>
                    <p className="text-white/60 mt-4">{t.checkout.bank_instructions}</p>
                </div>
                <div className="luxury-card p-8 space-y-4">
                    <Row label={t.checkout.order_number} value={orderId} />
                    <div className="hairline" />
                    <Row label={lang === "ar" ? "اسم البنك" : "Bank Name"} value={bankInfo.bank_name} />
                    <Row label={lang === "ar" ? "اسم الحساب" : "Account Name"} value={bankInfo.account_name} />
                    <Row label={lang === "ar" ? "رقم الآيبان" : "IBAN"} value={bankInfo.iban} />
                    <Row label={lang === "ar" ? "SWIFT" : "SWIFT"} value={bankInfo.swift} />
                </div>
                <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                    <a href={buildOrderWhatsAppUrl(orderId, "bank_transfer")} target="_blank" rel="noreferrer" data-testid="bank-whatsapp-btn" className="btn-gold btn-gold-filled">
                        {lang === "ar" ? "📱 إرسال تفاصيل الطلب عبر واتساب" : "📱 Send Order via WhatsApp"}
                    </a>
                    <button onClick={() => navigate("/")} className="btn-gold">
                        {t.success.back_home}
                    </button>
                </div>
                <p className="text-white/40 text-xs text-center mt-4">
                    {lang === "ar"
                        ? "اضغط الزر لإرسال كل تفاصيل طلبك للبائع عبر واتساب بضغطة واحدة."
                        : "Click the button to send all your order details to the seller via WhatsApp in one tap."}
                </p>
            </div>
        );
    }

    if (cashInfo && orderId) {
        return (
            <div className="pt-32 pb-24 max-w-2xl mx-auto px-6 lg:px-10" data-testid="cash-info">
                <div className="text-center mb-10">
                    <div className="overline mb-4">{t.checkout.order_placed}</div>
                    <h1 className="section-heading text-4xl md:text-5xl text-white mb-3">
                        {lang === "ar" ? "الدفع كاش عند الاستلام" : "Cash on Delivery"}
                    </h1>
                    <p className="text-white/60 mt-4">
                        {lang === "ar"
                            ? "تم استلام طلبك. اضغط زر واتساب أدناه لإرسال تفاصيل طلبك للبائع مباشرة."
                            : "Your order has been received. Click the WhatsApp button below to send your order details to the seller."}
                    </p>
                </div>
                <div className="luxury-card p-8 space-y-4">
                    <Row label={t.checkout.order_number} value={orderId} />
                    <div className="hairline" />
                    <Row label={lang === "ar" ? "الإجمالي المستحق" : "Total Due"} value={`${cashInfo.total} ${t.product.currency}`} />
                    <Row label={lang === "ar" ? "طريقة الدفع" : "Payment"} value={lang === "ar" ? "كاش عند الاستلام" : "Cash on Delivery"} />
                </div>
                <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                    <a href={buildOrderWhatsAppUrl(orderId, "cash")} target="_blank" rel="noreferrer" data-testid="cash-whatsapp-btn" className="btn-gold btn-gold-filled">
                        {lang === "ar" ? "📱 إرسال تفاصيل الطلب عبر واتساب" : "📱 Send Order via WhatsApp"}
                    </a>
                    <button onClick={() => navigate("/")} className="btn-gold">
                        {t.success.back_home}
                    </button>
                </div>
                <p className="text-white/40 text-xs text-center mt-4">
                    {lang === "ar"
                        ? "اضغط الزر لإرسال كل تفاصيل طلبك للبائع عبر واتساب بضغطة واحدة."
                        : "Click the button to send all your order details to the seller via WhatsApp in one tap."}
                </p>
            </div>
        );
    }

    return (
        <div data-testid="checkout-page" className="pt-32 pb-24 max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="mb-12">
                <div className="overline mb-3">{lang === "ar" ? "الخطوة الأخيرة" : "Final Step"}</div>
                <h1 className="section-heading text-4xl md:text-5xl text-white">{t.checkout.title}</h1>
            </div>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 space-y-10">
                    <section>
                        <h2 className="text-xl font-serif text-white mb-6">{t.checkout.customer_info}</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input required placeholder={t.checkout.name} data-testid="input-name" className="lux-input" value={form.customer_name} onChange={update("customer_name")} />
                            <input required type="email" placeholder={t.checkout.email} data-testid="input-email" className="lux-input" value={form.customer_email} onChange={update("customer_email")} />
                            <input required placeholder={t.checkout.phone} data-testid="input-phone" className="lux-input" value={form.customer_phone} onChange={update("customer_phone")} />
                            <input required placeholder={t.checkout.city} data-testid="input-city" className="lux-input" value={form.customer_city} onChange={update("customer_city")} />
                            <input required placeholder={t.checkout.address} data-testid="input-address" className="lux-input md:col-span-2" value={form.customer_address} onChange={update("customer_address")} />
                            <textarea placeholder={t.checkout.notes} data-testid="input-notes" className="lux-input md:col-span-2" rows={3} value={form.notes} onChange={update("notes")} />
                        </div>
                    </section>

                    <section>
                        <h2 className="text-xl font-serif text-white mb-6">{t.checkout.payment_method}</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <PaymentOption
                                active={selectedMethod === "card"}
                                onClick={() => pickMethod("card")}
                                icon={<CreditCard size={20} />}
                                label={lang === "ar" ? "بطاقة بنكية" : "Bank Card"}
                                testid="pay-method-stripe"
                            />
                            <PaymentOption
                                active={selectedMethod === "applepay"}
                                onClick={() => pickMethod("applepay")}
                                icon={
                                    <svg width="22" height="22" viewBox="0 0 170 170" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.2-2.12-9.98-3.17-14.35-3.17-4.58 0-9.5 1.05-14.76 3.17-5.27 2.13-9.51 3.24-12.76 3.35-4.93.21-9.84-1.96-14.76-6.52-3.13-2.73-7.05-7.41-11.74-14.04-5.03-7.08-9.17-15.29-12.41-24.65-3.47-10.11-5.21-19.9-5.21-29.38 0-10.86 2.35-20.23 7.05-28.08 3.69-6.31 8.6-11.28 14.76-14.92 6.15-3.64 12.81-5.5 19.98-5.62 3.91 0 9.05 1.21 15.41 3.59 6.34 2.39 10.41 3.6 12.19 3.6 1.33 0 5.85-1.41 13.52-4.24 7.25-2.62 13.38-3.71 18.41-3.29 13.61 1.1 23.84 6.47 30.66 16.13-12.18 7.37-18.2 17.7-18.08 30.95.11 10.32 3.86 18.91 11.23 25.73 3.34 3.17 7.07 5.62 11.22 7.37-.9 2.61-1.85 5.11-2.86 7.51zM119.32 7.5c0 8.1-2.96 15.67-8.86 22.69-7.12 8.34-15.73 13.16-25.07 12.4a25.24 25.24 0 01-.19-3.07c0-7.78 3.39-16.12 9.4-22.94 3.01-3.45 6.85-6.32 11.52-8.61 4.65-2.25 9.04-3.49 13.17-3.71.13 1.07.19 2.14.19 3.21z"/>
                                    </svg>
                                }
                                label="Apple Pay"
                                testid="pay-method-applepay"
                            />
                            <PaymentOption
                                active={selectedMethod === "bank_transfer"}
                                onClick={() => pickMethod("bank_transfer")}
                                icon={<Landmark size={20} />}
                                label={t.checkout.pay_bank}
                                testid="pay-method-bank"
                            />
                            <PaymentOption
                                active={selectedMethod === "cash"}
                                onClick={() => pickMethod("cash")}
                                icon={<Banknote size={20} />}
                                label={lang === "ar" ? "كاش عند الاستلام" : "Cash on Delivery"}
                                testid="pay-method-cash"
                            />
                        </div>
                        {selectedMethod === "card" && (
                            <p data-testid="stripe-hint" className="text-white/50 text-xs mt-3 leading-relaxed">
                                {lang === "ar"
                                    ? "ستُحوَّل لصفحة دفع آمنة من Stripe لإتمام الدفع ببطاقتك البنكية. كل البطاقات الإماراتية والعالمية مقبولة."
                                    : "You'll be redirected to Stripe's secure payment page to complete the payment with your bank card. All UAE and international cards are accepted."}
                            </p>
                        )}
                        {selectedMethod === "applepay" && (
                            <p data-testid="applepay-hint" className="text-white/50 text-xs mt-3 leading-relaxed">
                                {lang === "ar"
                                    ? "ستُحوَّل لصفحة دفع آمنة من Stripe، وسيظهر زر Apple Pay تلقائياً عند استخدامك آيفون أو ماك مع البصمة / Face ID."
                                    : "You'll be redirected to Stripe's secure payment page. The Apple Pay button will appear automatically on iPhone, iPad, or Mac with Touch ID / Face ID."}
                            </p>
                        )}
                        {selectedMethod === "bank_transfer" && (
                            <p data-testid="bank-hint" className="text-white/50 text-xs mt-3 leading-relaxed">
                                {lang === "ar"
                                    ? "بعد تأكيد الطلب، ستظهر لك تفاصيل التحويل البنكي الكاملة. حوّل المبلغ ثم أرسل إيصال التحويل عبر واتساب لتأكيد الطلب."
                                    : "After placing your order, full bank transfer details will be shown. Transfer the amount then send the receipt via WhatsApp to confirm your order."}
                            </p>
                        )}
                        {selectedMethod === "cash" && (
                            <p data-testid="cash-hint" className="text-white/50 text-xs mt-3 leading-relaxed">
                                {lang === "ar"
                                    ? "ادفع نقداً عند وصول طلبك. سنتواصل معك عبر واتساب لتأكيد التفاصيل وتحديد موعد التسليم."
                                    : "Pay in cash when your order arrives. We'll contact you via WhatsApp to confirm details and arrange delivery."}
                            </p>
                        )}
                    </section>
                </div>

                <aside className="luxury-card p-8 h-fit sticky top-28">
                    <h3 className="text-lg font-serif text-white mb-6">{t.checkout.order_summary}</h3>
                    <ul className="space-y-4 mb-6">
                        {cart.map((it) => (
                            <li key={`${it.product_id}-${it.variant_label || ''}`} className="flex justify-between gap-3 text-sm">
                                <div className="text-white/80">
                                    <p>{it.product_name}</p>
                                    <p className="text-white/50 text-xs">{it.variant_label ? `${it.variant_label} × ` : ''}{it.quantity}</p>
                                </div>
                                <span className="text-white price-display">{fmtPrice(it.price * it.quantity, lang)}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="hairline my-6" />
                    <div className="flex items-center justify-between mb-6">
                        <span className="overline">{t.checkout.total}</span>
                        <span data-testid="checkout-total" className="price-display text-2xl text-[#D4AF37]">{fmtPrice(cartTotal, lang)}</span>
                    </div>
                    <button type="submit" disabled={loading || cart.length === 0} data-testid="place-order-btn" className="btn-gold btn-gold-filled w-full disabled:opacity-50">
                        {loading ? (lang === "ar" ? "جارٍ المعالجة..." : "Processing...") : t.checkout.place_order}
                    </button>
                </aside>
            </form>
        </div>
    );
}

const PaymentOption = ({ active, onClick, icon, label, testid, disabled = false }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`flex items-center gap-3 p-5 border text-start transition-all ${
            disabled
                ? "border-[#D4AF37]/15 opacity-50 cursor-not-allowed"
                : active
                    ? "border-[#D4AF37] bg-[#D4AF37]/10"
                    : "border-[#D4AF37]/30 hover:border-[#D4AF37]/60"
        }`}
    >
        <span className={`${disabled ? "text-white/40" : active ? "text-[#D4AF37]" : "text-white/70"}`}>{icon}</span>
        <span className={`text-sm flex-1 ${disabled ? "text-white/50" : active ? "text-[#D4AF37]" : "text-white"}`}>{label}</span>
    </button>
);

const ApplePayOption = ({ active, onClick, testid }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`flex items-center justify-center gap-2 p-5 border transition-all bg-black ${
            active ? "border-[#D4AF37]" : "border-[#D4AF37]/30 hover:border-white/60"
        }`}
    >
        <svg width="22" height="26" viewBox="0 0 170 170" fill="white" xmlns="http://www.w3.org/2000/svg">
            <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.2-2.12-9.98-3.17-14.35-3.17-4.58 0-9.5 1.05-14.76 3.17-5.27 2.13-9.51 3.24-12.76 3.35-4.93.21-9.84-1.96-14.76-6.52-3.13-2.73-7.05-7.41-11.74-14.04-5.03-7.08-9.17-15.29-12.41-24.65-3.47-10.11-5.21-19.9-5.21-29.38 0-10.86 2.35-20.23 7.05-28.08 3.69-6.31 8.6-11.28 14.76-14.92 6.15-3.64 12.81-5.5 19.98-5.62 3.91 0 9.05 1.21 15.41 3.59 6.34 2.39 10.41 3.6 12.19 3.6 1.33 0 5.85-1.41 13.52-4.24 7.25-2.62 13.38-3.71 18.41-3.29 13.61 1.1 23.84 6.47 30.66 16.13-12.18 7.37-18.2 17.7-18.08 30.95.11 10.32 3.86 18.91 11.23 25.73 3.34 3.17 7.07 5.62 11.22 7.37-.9 2.61-1.85 5.11-2.86 7.51zM119.32 7.5c0 8.1-2.96 15.67-8.86 22.69-7.12 8.34-15.73 13.16-25.07 12.4a25.24 25.24 0 01-.19-3.07c0-7.78 3.39-16.12 9.4-22.94 3.01-3.45 6.85-6.32 11.52-8.61 4.65-2.25 9.04-3.49 13.17-3.71.13 1.07.19 2.14.19 3.21z"/>
        </svg>
        <span className="text-white text-lg font-medium tracking-tight">Pay</span>
    </button>
);

const Row = ({ label, value }) => (
    <div className="flex items-center justify-between gap-4">
        <span className="text-white/50 text-sm">{label}</span>
        <span className="text-white font-medium text-sm break-all text-end">{value}</span>
    </div>
);
